// This file has been modified to remove the Byte Order Mark (BOM).
// Ensure that the file is saved without BOM to pass linting.

import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Pagination,
} from "react-bootstrap";

import api, { resolvePendampingDocUrl } from "../../services/api";
import "./Dashboard.css";

const PAGE_SIZE = 20;

const PesertaPelatihan = () => {
  const [pesertaList, setPesertaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevSearchRef = useRef("");
  const [provinsiList, setProvinsiList] = useState([]);
  const [selectedProvinsi, setSelectedProvinsi] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(null);

  useEffect(() => {
    fetchProvinsi();
  }, []);

  // Debounce input pencarian -> reset ke halaman 1
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (prevSearchRef.current === trimmed) return;
      prevSearchRef.current = trimmed;
      setDebouncedSearch(trimmed);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchPeserta(currentPage, debouncedSearch, selectedProvinsi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, selectedProvinsi]);

  const fetchProvinsi = async () => {
    try {
      const response = await api.get("/reference/provinsi");
      if (response.data.success) {
        setProvinsiList(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching provinsi:", err);
    }
  };

  const getFileUrl = (folder, filename, fullUrl) =>
    resolvePendampingDocUrl(folder, filename, fullUrl);

  const fetchPeserta = async (
    page = currentPage,
    search = debouncedSearch,
    provinsi = selectedProvinsi,
  ) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (provinsi) params.provinsi = provinsi;
      const response = await api.get("/lp/peserta-pelatihan", { params });
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const total =
        data?.total ??
        data?.pagination?.total ??
        (list.length < PAGE_SIZE && page === 1 ? list.length : 0);
      setPesertaList(list);
      setTotalRecords(
        total > 0
          ? total
          : list.length >= PAGE_SIZE
            ? page * PAGE_SIZE + 1
            : (page - 1) * PAGE_SIZE + list.length,
      );
    } catch (err) {
      console.error("Error fetching peserta:", err);
      setError(
        err.response?.data?.error ||
          "Gagal memuat data peserta pelatihan. Silakan coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProvinsiChange = (e) => {
    setSelectedProvinsi(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const showPagination = totalRecords > PAGE_SIZE || currentPage > 1;

  const renderPagination = (position = "bottom") => {
    if (!showPagination) return null;

    const marginClass = position === "top" ? "mb-3" : "mt-3";
    const pageItems = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(
        (p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2,
      )
      .reduce((acc, p, i, arr) => {
        if (i > 0 && p - arr[i - 1] > 1) acc.push("ellipsis");
        acc.push(p);
        return acc;
      }, []);

    return (
      <div
        className={`d-flex justify-content-between align-items-center flex-wrap gap-2 ${marginClass}`}
      >
        <small className="text-muted">
          Halaman <strong>{currentPage}</strong> dari{" "}
          <strong>{totalPages.toLocaleString("id-ID")}</strong>
          {totalRecords > 0 && (
            <> ({totalRecords.toLocaleString("id-ID")} data)</>
          )}
        </small>
        <Pagination className="mb-0 flex-wrap">
          <Pagination.Prev
            disabled={currentPage <= 1 || loading}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          />
          {pageItems.map((item, idx) =>
            item === "ellipsis" ? (
              <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
            ) : (
              <Pagination.Item
                key={item}
                active={item === currentPage}
                disabled={loading}
                onClick={() => setCurrentPage(item)}
              >
                {item}
              </Pagination.Item>
            ),
          )}
          <Pagination.Next
            disabled={currentPage >= totalPages || loading}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        </Pagination>
      </div>
    );
  };

  const handleResetPassword = (peserta) => {
    setResetPasswordTarget(peserta);
    setNewPassword("");
    setConfirmPassword("");
    setResetPasswordError(null);
    setShowResetPasswordModal(true);
  };

  const handleKonfirmasiResetPassword = async () => {
    if (!resetPasswordTarget) return;
    if (!newPassword) {
      setResetPasswordError("Password baru tidak boleh kosong.");
      return;
    }
    if (newPassword.length < 6) {
      setResetPasswordError("Password minimal 6 karakter.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetPasswordError("Konfirmasi password tidak cocok.");
      return;
    }
    setResetPasswordLoading(true);
    setResetPasswordError(null);
    try {
      const response = await api.post(
        "/pendamping/reset-password-local",
        {
          id_pendamping: resetPasswordTarget.id_pendamping,
          new_password: newPassword,
        },
      );
      if (response.data.success) {
        setShowResetPasswordModal(false);
        setSuccessMessage(
          `Password untuk ${resetPasswordTarget.nama} berhasil direset.`,
        );
      } else {
        setResetPasswordError(
          response.data.message || "Gagal mereset password.",
        );
      }
    } catch (err) {
      setResetPasswordError(
        err.response?.data?.message || "Gagal menghubungi server.",
      );
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div>
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-users"></i>
            </div>
            <div>
              <h1 className="page-header-title">Daftar Peserta Pelatihan</h1>
              <p className="page-header-subtitle">
                Kelola dan monitoring peserta pelatihan pendamping
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="row mb-3 align-items-center">
            <div className="col-md-5 mb-2 mb-md-0">
              <Form.Control
                type="text"
                placeholder="Cari nama, email, no HP..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-2 mb-md-0">
              <Form.Select
                value={selectedProvinsi}
                onChange={handleProvinsiChange}
              >
                <option value="">-- Semua Provinsi --</option>
                {provinsiList.map((p) => (
                  <option key={p.kodeprovinsi} value={p.namaprovinsi}>
                    {p.namaprovinsi}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3 text-end">
              <Button
                variant="primary"
                onClick={() =>
                  fetchPeserta(currentPage, debouncedSearch, selectedProvinsi)
                }
              >
                <i className="fas fa-sync-alt me-2" /> Refresh
              </Button>
            </div>
          </div>

          {!loading && renderPagination("top")}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Nama</th>
                    <th>No HP</th>
                    <th>Email</th>
                    <th>NIK</th>
                    <th>Provinsi</th>
                    <th>Kabupaten</th>
                    <th>Tgl Daftar</th>
                    <th>KTP</th>
                    <th>Ijazah</th>
                    <th>Foto Diri</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pesertaList.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="text-center">
                        Data peserta pelatihan tidak ditemukan
                      </td>
                    </tr>
                  ) : (
                    pesertaList.map((item, index) => (
                      <tr key={item.id_pendamping || item.id || index}>
                        <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                        <td>{item.nama || "-"}</td>
                        <td>{item.no_hp || "-"}</td>
                        <td>{item.email || "-"}</td>
                        <td>{item.nik || "-"}</td>
                        <td>{item.namaprovinsi || "-"}</td>
                        <td>{item.namakabupaten || "-"}</td>
                        <td>
                          {formatDate(item.created_on || item.tgl_daftar)}
                        </td>
                        <td>
                          {item.fotoktp ? (
                            <a
                              href={getFileUrl("ktp", item.fotoktp, item.fotoktp_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              KTP
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {item.fotoijazah ? (
                            <a
                              href={getFileUrl("ijazah", item.fotoijazah, item.fotoijazah_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              Ijazah
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {item.filefoto ? (
                            <a
                              href={getFileUrl("foto", item.filefoto, item.filefoto_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              Foto
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleResetPassword(item)}
                          >
                            Reset Password
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {!loading && renderPagination("bottom")}
        </Card.Body>
      </Card>
      <Modal
        show={showResetPasswordModal}
        onHide={() => !resetPasswordLoading && setShowResetPasswordModal(false)}
        centered
      >
        <Modal.Header closeButton={!resetPasswordLoading}>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Reset password untuk: <strong>{resetPasswordTarget?.nama}</strong>
          </p>
          {resetPasswordError && (
            <Alert variant="danger" className="py-2">
              {resetPasswordError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Password Baru</Form.Label>
              <Form.Control
                type="password"
                placeholder="Masukkan password baru (min. 6 karakter)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetPasswordLoading}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Konfirmasi Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ulangi password baru"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={resetPasswordLoading}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowResetPasswordModal(false)}
            disabled={resetPasswordLoading}
          >
            Batal
          </Button>
          <Button
            variant="warning"
            onClick={handleKonfirmasiResetPassword}
            disabled={resetPasswordLoading}
          >
            {resetPasswordLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Memproses...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PesertaPelatihan;
